import { IRecordDeprecatedService } from '@agent/core/phone/record-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ChannelDeprecatedImpl as ChannelUtil } from '@commons/models/channel';
import { IContact } from '@commons/models/contact';
import { IContactEntry } from '@commons/models/contact-entry';
import { RxUtil } from '@commons/util/utilities/rx';
import { ILogService, IPromise, IQService, isArray, isDefined } from 'angular';
import moment from 'moment';
import { catchError, delay, map, Observable, of, retryWhen, scan, tap, throwError } from 'rxjs';

import { ChanEnum, Channel, Civility, ElasticSearchService, initialBooking, MailService } from '@contact/upgrade';

export const RESPONSE_AUTO_UUID = 'RESPONSE_AUTO';
export const RESPONSE_AUTO_NAME = 'RESPONSEAUTO';
export const EXTERNAL_ROUTING_NAME = 'EXTERNAL_ROUTING';

interface ContactFilter {
  lastNameFilter: string;
  firstNameFilter: string;
  repositoryName: string;
  phoneFilter: string;
  mailFilter: string;
  contactIdFilter: string;
}

interface ContactHistory {
  uuid: string;
  firstname: string;
  lastname: string;
  companyname: string;
  civility: Civility;
  contactId: string;
  description: string;
  assignedCsrId: string;
  blackListedCsrID: string[];
  blackListedCsr: string[];
  extraData: { key: string; value: string }[];
  assignedCsr: any;
  creationDate: string;
  addresses: any[];
  phoneNumber_1: string;
  phoneNumber_2: string;
  phoneNumber_3: string;
  mail_1: string;
  mail_2: string;
  customerRepositoryName: string;
  checked?: boolean;
  contact: IContact | null;
  consent: boolean;
}

interface ContactHistoryResult {
  contactHistory: ContactHistory[];
  total: number;
}

interface CsrHistory {
  uuid: string;
  name: string;
  beginDateTime?: string;
  calledUri: string;
  calledName: string;
  callDuration: string;
  technicalId: string;
}

interface InteractionResult {
  contactEntries: Interaction[];
  total: number;
}

interface Interaction extends IContactEntry {
  hasRecord: boolean;
  record: any;
  sort: number;
}

export interface IHistoryDeprecatedService {
  searchContactList(
    filter: ContactFilter,
    from?,
    size?,
    repositoryNames?: string[],
    sortName?: string,
    sortOrder?: string
  ): Observable<ContactHistoryResult>;
  getExtraFields();
  mergeContacts(contactUuid: string, slavedContactIds: string[]);
  initPhoneAndMailFromAddresses(contact, addresses);
  getPerimeters(): Observable<any>;
  execCsrRequest(json, domainUuidList, teamsUuid): Observable<CsrHistory[]>;
  deleteContact(contactId);
  initEpticaService(): IPromise<any>;
  importContacts(json);
  getInteractionOfContact(json);
  getInteractionOfContactPrimary(json);
  getDomainsOfPerimeter(): IPromise<any>;
  getRepositoryNames(domainUuid: string): IPromise<any>;
  execContactEntryRequest(
    json,
    from,
    size,
    flowsUuid,
    skillsUuid,
    domainUuidList,
    teamsUuid,
    sortName: string,
    sortOrder: string
  ): Observable<InteractionResult>;
  execSearchRecordFromInteractionRequest(json): Observable<CsrHistory | undefined>;
  isRecordAvailable(technicalId: string): Observable<boolean>;
  execContactRequest(json, from?, size?): Observable<ContactHistory[]>;
  buildContact(hit): IContact;
  buildContactHistory(data): ContactHistory[];
}

export class HistoryDeprecatedService implements IHistoryDeprecatedService {
  static $inject: Array<string> = ['$log', '$q', 'httpDeprecated', 'elasticSearchService', 'recordDeprecatedService', 'mailService'];

  private contactLength: number = -1;
  private newContactLength: number = -1;

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly recordDeprecatedService: IRecordDeprecatedService,
    private readonly mailService: MailService
  ) {}

  // initialize the eptica part
  initEpticaService(): IPromise<any> {
    return RxUtil.toPromise(this.mailService.initialize(), this.$q);
  }

  buildContact(hit): IContact {
    let toReturn = initialBooking;
    if (isDefined(hit) && hit != null) {
      toReturn = {
        ...toReturn,
        responseId: hit._source.uuid || '',
        customerName: hit._source.contactLastName || hit._source.lastname || '',
      };
    }
    return toReturn;
  }

  buildContactHistory(data): ContactHistory[] {
    let toReturn: ContactHistory[] = [];

    if (
      !!data &&
      isDefined(data.hits) &&
      isDefined(data.hits.total) &&
      isDefined(data.hits.total.value) &&
      data.hits.total.value > 0 &&
      isArray(data.hits.hits)
    ) {
      toReturn = data.hits.hits.map(hit => {
        let mappedValue: ContactHistory;
        mappedValue = {
          uuid: hit._source.uuid || '',
          firstname: hit._source.contactFirstName || hit._source.firstname || '',
          lastname: hit._source.contactLastName || hit._source.lastname || '',
          companyname: hit._source.companyname || '',
          civility: hit._source.civility || '',
          contactId: hit._source.contactId || '',
          description: hit._source.description || '',
          assignedCsrId: hit._source.assignedCsrId || '',
          blackListedCsrID: hit._source.blackList || [],
          blackListedCsr: hit._source.blackListedCsr || [],
          extraData: hit._source.extraData || [],
          creationDate: hit._source.creationDate || '',
          assignedCsr: hit._source.assignedCsr,
          addresses: hit._source.addresses,
          phoneNumber_1: '',
          phoneNumber_2: '',
          phoneNumber_3: '',
          mail_1: '',
          mail_2: '',
          customerRepositoryName: hit._source.customerRepositoryName,
          contact: this.buildContact(hit),
          consent: hit._source.consent,
        };
        this.initPhoneAndMailFromAddresses(mappedValue, hit._source.addresses);
        return mappedValue;
      });
    }

    return toReturn;
  }

  initPhoneAndMailFromAddresses(contact, addresses) {
    let phones: any = [];
    let mails: any = [];

    if (addresses != null) {
      addresses.forEach(address => {
        if (address.channel === 'PHONE_INCOMING') {
          phones.push(address.uri);
        }
        if (address.channel === 'MAIL_INCOMING') {
          mails.push(address.uri);
        }
      });
    }
    contact.phoneNumber_1 = contact.phoneNumber_1 || phones[0] || '';
    contact.phoneNumber_2 = contact.phoneNumber_2 || phones[1] || '';
    contact.phoneNumber_3 = contact.phoneNumber_3 || phones[2] || '';
    contact.mail_1 = contact.mail_1 || mails[0] || '';
    contact.mail_2 = contact.mail_2 || mails[1] || '';
  }

  private buildCsrList(data): CsrHistory[] {
    if (
      !!data &&
      isDefined(data.hits) &&
      isDefined(data.hits.total) &&
      isDefined(data.hits.total.value) &&
      data.hits.total.value > 0 &&
      isArray(data.hits.hits)
    ) {
      return data.hits.hits.map(hit => ({
        uuid: hit._source.uuid || '',
        name: hit._source.firstName + ' ' + hit._source.lastName || '',
        firstName: hit._source.firstName,
        lastName: hit._source.lastName,
        beginDateTime: hit._source.beginDate || '',
        calledUri: hit._source.calledUri || '',
        calledName: hit._source.calledName || '',
        callDuration: hit._source.callDuration && hit._source.callDuration > 0 ? hit._source.callDuration : '',
        technicalId: data.channelId || '',
      }));
    }
    return [];
  }

  searchContactList(
    filter: ContactFilter,
    from = 0,
    size = 10,
    repositoryNames: string[],
    sortName = 'creationDate',
    sortOrder = 'desc'
  ): Observable<ContactHistoryResult> {
    let searchParams: any = this.buildContactRequest(filter, (from - 1) * size, size, repositoryNames, sortName, sortOrder);

    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ({
        contactHistory: this.buildContactHistory(data),
        total: !!data && isDefined(data.hits) && isDefined(data.hits.total) ? data.hits.total.value : 0,
      })),
      tap(next => (this.contactLength = next.total)),
      map(response => {
        if (this.newContactLength > 0 && this.contactLength !== this.newContactLength) {
          throw 'contact data are not up to date';
        }
        this.newContactLength = -1;
        return response;
      }),
      retryWhen(errors =>
        errors.pipe(
          // log error message
          tap(() => this.$log.info(`contact length too small! ${this.newContactLength}-${this.contactLength}`)),
          // restart in 2 seconds
          delay(2000),
          scan((errorCount, err) => {
            if (errorCount > 4) {
              throw err;
            }
            return errorCount + 1;
          }, 0)
        )
      ),
      catchError(error => throwError(() => new Error(`[HistoryService] searchContactList failed :[${JSON.stringify(error)}]`)))
    );
  }

  private buildOneContactByUuidRequest(contactUuid) {
    return {
      types: ['contact'],
      query: {
        bool: {
          must: { term: { uuid: contactUuid } },
        },
      },
    };
  }

  private buildContactRequest(data, from, size, repositoryNames?: string[], sortName = 'creationDate', sortOrder = 'desc') {
    let query: any = {};
    query.types = ['contact'];
    query.size = size;
    query.from = from;
    query.track_total_hits = true;
    let sortField = {};
    sortField[sortName] = { order: sortOrder };
    query.sort = sortField;
    query.query = {};

    let repositoryNamesInQuery: any[] = [];
    if (data.repositoryName !== '') {
      repositoryNamesInQuery.push(data.repositoryName);
    } else if (repositoryNames && repositoryNames.length > 0) {
      repositoryNamesInQuery = repositoryNames.slice();
    } else {
      repositoryNamesInQuery = ['default'];
    }

    if (
      !data.firstNameFilter &&
      !data.lastNameFilter &&
      !data.phoneFilter &&
      !data.mailFilter &&
      !data.contactIdFilter &&
      !data.uuidFilter
    ) {
      query.query.terms = {
        'customerRepositoryName.keyword': repositoryNamesInQuery,
      };
      return query;
    } else {
      let csrQuery;
      let finalQuery = {
        types: ['contact'],
        track_total_hits: true,
        query: {
          bool: {
            must: {
              terms: { 'customerRepositoryName.keyword': repositoryNamesInQuery },
            },
            filter: {
              bool: {
                must: new Array(),
              },
            },
          },
        },
        size: size,
        from: from,
        sort: {},
      };
      finalQuery.sort = sortField;

      if (data.firstNameFilter) {
        csrQuery = {
          bool: {
            should: [
              {
                regexp: {
                  'contactFirstName.keyword': `.*${data.firstNameFilter.toLowerCase()}.*`,
                },
              },
            ],
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }

      if (data.lastNameFilter) {
        csrQuery = {
          bool: {
            should: [
              {
                regexp: {
                  'contactLastName.keyword': `.*${data.lastNameFilter.toLowerCase()}.*`,
                },
              },
            ],
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }

      if (data.phoneFilter) {
        csrQuery = {
          bool: {
            must: [
              {
                regexp: {
                  'addresses.uri': '.*' + data.phoneFilter + '.*',
                },
              },
              {
                term: {
                  'addresses.channel': 'PHONE_INCOMING',
                },
              },
            ],
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }

      if (data.mailFilter) {
        csrQuery = {
          bool: {
            must: [
              {
                regexp: {
                  'addresses.uri': `.*${data.mailFilter.toLowerCase()}.*`,
                },
              },
              {
                term: {
                  'addresses.channel': 'MAIL_INCOMING',
                },
              },
            ],
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }
      if (data.contactIdFilter) {
        csrQuery = {
          bool: {
            should: {
              prefix: {
                contactId: data.contactIdFilter.toLowerCase(),
              },
            },
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }

      if (data.uuidFilter) {
        csrQuery = {
          bool: {
            must: [
              {
                term: {
                  uuid: data.uuidFilter,
                },
              },
            ],
          },
        };
        finalQuery.query.bool.filter.bool.must.push(csrQuery);
      }

      return finalQuery;
    }
  }

  private buildCsrRequest(text, domainUuidList, teamsUuid) {
    if (text) {
      return {
        types: ['user'],
        size: 50,
        sort: [
          {
            'lastName.keyword': {
              order: 'asc',
            },
          },
        ],
        query: {
          bool: {
            must: {
              bool: {
                must: [
                  {
                    term: {
                      functionsNames: 'CSR',
                    },
                  },
                  {
                    bool: {
                      should: [
                        {
                          prefix: {
                            'firstName.keyword': text.toLowerCase(),
                          },
                        },
                        {
                          prefix: {
                            'lastName.keyword': text.toLowerCase(),
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
            filter: {
              bool: {
                should: [
                  {
                    bool: {
                      must: [
                        {
                          bool: {
                            must_not: {
                              exists: { field: 'team.uuid' },
                            },
                          },
                        },
                        {
                          terms: { domainUuid: domainUuidList },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          terms: { 'team.uuid': teamsUuid },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          },
        },
      };
    } else {
      return {
        types: ['user'],
        size: 50,
        sort: [
          {
            'lastName.keyword': {
              order: 'asc',
            },
          },
        ],
        query: {
          bool: {
            must: [
              { match_all: {} },
              {
                term: {
                  functionsNames: 'CSR',
                },
              },
            ],
            filter: {
              bool: {
                should: [
                  {
                    bool: {
                      must: [
                        {
                          bool: {
                            must_not: {
                              exists: { field: 'team.uuid' },
                            },
                          },
                        },
                        {
                          terms: { domainUuid: domainUuidList },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          terms: { 'team.uuid': teamsUuid },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          },
        },
      };
    }
  }

  getPerimeters(): Observable<any> {
    return RxUtil.fromPromise(() => this.httpDeprecated.asPromise().get('protected/user/getPerimeters', ['HISTORIZATION'])).pipe(
      map((response: any) => response.data)
    );
  }

  execCsrRequest(json, domainUuidList, teamsUuid): Observable<CsrHistory[]> {
    if (!json) {
      json.text = '';
    }
    let searchParams: any = this.buildCsrRequest(json.text, domainUuidList, teamsUuid);
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(response => this.buildCsrList(response)),
      catchError(error => throwError(() => new Error(`[HistoryService] execCsrRequest failed :[${JSON.stringify(error)}]`)))
    );
  }

  getExtraFields() {
    return this.httpDeprecated.asPromise().get('protected/historization/getCustomFields');
  }

  mergeContacts(contactUuid: string, slavedContactIds: string[]) {
    if (contactUuid) {
      this.newContactLength = this.contactLength - slavedContactIds.length;
    }
    return this.httpDeprecated
      .asPromise()
      .withData(slavedContactIds)
      .post('V2/protected/historization/contacts/' + contactUuid + '/merge/run/true');
  }

  deleteContact(contactId) {
    if (contactId) {
      this.newContactLength = this.contactLength - 1;
    }
    return this.httpDeprecated.asPromise().delete('V2/protected/historization/contacts/contact', contactId);
  }

  importContacts(json) {
    if (json) {
      this.newContactLength = this.contactLength + json.length;
    }

    return this.httpDeprecated.asPromise().withData(json).post('V2/protected/historization/contacts/import');
  }

  getInteractionOfContact(contactUuid) {
    let begin = moment();
    let end = moment();
    begin.month(begin.month() - 1);

    let json = {
      begin: begin.format(),
      end: end.format(),
      contactId: contactUuid,
    };

    return this.httpDeprecated.asPromise().withData(json).post('V2/protected/historization/interactions/byRequestInteraction');
  }

  getInteractionOfContactPrimary(contactUuid) {
    let begin = moment();
    let end = moment();
    begin.month(begin.month() - 1);

    let json = {
      begin: begin.format(),
      end: end.format(),
      contactId: contactUuid,
    };

    return this.httpDeprecated.asPromise().withData(json).post('V2/protected/historization/interactions/byRequestInteractionPrimary');
  }

  getDomainsOfPerimeter() {
    return this.httpDeprecated.asPromise().get('protected/user/getDomainsOfPerimeter', ['HISTORIZATION']);
  }

  getRepositoryNames(domain: string) {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
          Accept: 'text/plain; charset=UTF-8',
        },
      })
      .get('facade/public/tools/getCustomerRepositoryNameFromDomain', [domain]);
  }

  private buildContactEntryRequest(
    data,
    from,
    size,
    flowsUuid,
    skillsUuid,
    domainUuidList,
    teamsUuid,
    sortName: string,
    sortOrder: string
  ) {
    let sortField = {};

    if (sortName === 'contactLastName.keyword') {
      sortField = [{ 'contactLastName.keyword': { order: sortOrder } }, { 'contactFirstName.keyword': { order: sortOrder } }];
    } else if (sortName === 'canalName') {
      sortField = [{ canalName: { order: sortOrder } }, { canalDirection: { order: sortOrder } }];
    } else {
      sortField[sortName] = { order: sortOrder };
    }

    if (
      !data.channelFilter &&
      !data.beginDate &&
      !data.endDate &&
      (!data.csr || !data.csr.uuid) &&
      !data.firstNameFilter &&
      !data.lastNameFilter &&
      !data.uriFilter
    ) {
      let query = {
        types: ['contact_entry'],
        track_total_hits: true,
        query: {
          bool: {
            should: [{ terms: { 'mediaEntryPoint.uuid': flowsUuid } }, { term: { canalName: 'SMS' } }],
            must_not: [{ terms: { 'skills.uuid': skillsUuid } }],
            filter: {
              bool: {
                should: [
                  {
                    bool: {
                      must: [
                        {
                          bool: {
                            must_not: {
                              exists: { field: 'csr.team.uuid' },
                            },
                          },
                        },
                        {
                          terms: { 'csr.domainUuid': domainUuidList },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          terms: { 'csr.team.uuid': teamsUuid },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          term: { csrUuid: RESPONSE_AUTO_UUID },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          term: { notHandledReason: 'DISSUADED' },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          },
        },
        sort: {},
        size: size,
        from: from,
      };

      query.sort = sortField;
      return query;
    } else {
      let query = {
        types: ['contact_entry'],
        track_total_hits: true,
        query: {
          bool: {
            must: {
              bool: {
                should: [{ terms: { 'mediaEntryPoint.uuid': flowsUuid } }, { term: { canalName: 'SMS' } }],
                must_not: [{ terms: { 'skills.uuid': skillsUuid } }],
                filter: {
                  bool: {
                    must: new Array(),
                  },
                },
              },
            },
            filter: {
              bool: {
                should: [
                  {
                    bool: {
                      must: [
                        {
                          bool: {
                            must_not: {
                              exists: { field: 'csr.team.uuid' },
                            },
                          },
                        },
                        {
                          terms: { 'csr.domainUuid': domainUuidList },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          terms: { 'csr.team.uuid': teamsUuid },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          term: { csrUuid: RESPONSE_AUTO_UUID },
                        },
                      ],
                    },
                  },
                  {
                    bool: {
                      must: [
                        {
                          term: { notHandledReason: 'DISSUADED' },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          },
        },
        sort: {},
        size: size,
        from: from,
      };

      query.sort = sortField;

      if (data.csr && data.csr.uuid !== undefined) {
        let csrQuery = {
          bool: {
            must: [
              {
                term: {
                  csrUuid: data.csr.uuid,
                },
              },
            ],
          },
        };
        query.query.bool.must.bool.filter.bool.must.push(csrQuery);
      }
      if (data.channelFilter) {
        let channel: Channel = ChannelUtil.from(data.channelFilter).getOrElse(ChanEnum.NotSet);
        if (!!channel) {
          let canalName = channel.type;
          let canalDirection = channel.direction;
          // specific case
          let channelQuery = {
            bool: {
              must: [
                {
                  term: {
                    canalName: canalName,
                  },
                },
                {
                  term: {
                    canalDirection: canalDirection,
                  },
                },
              ],
            },
          };
          query.query.bool.must.bool.filter.bool.must.push(channelQuery);
        }
      }

      if (data.campaignType) {
        let cammaignTypeQuery = {
          bool: {
            must: [
              {
                term: {
                  campaignType: data.campaignType,
                },
              },
            ],
          },
        };
        query.query.bool.must.bool.filter.bool.must.push(cammaignTypeQuery);
      }

      let dateOrQuery = {
        bool: {
          should: new Array(),
        },
      };

      if (data.beginDate && data.endDate) {
        let dateQueryBegin = {
          range: {
            entryDate: {
              from: data.beginDate,
              to: data.endDate,
            },
          },
        };
        let dateQueryEnd = {
          range: {
            endDate: {
              from: data.beginDate,
              to: data.endDate,
            },
          },
        };
        dateOrQuery.bool.should.push(dateQueryBegin);
        dateOrQuery.bool.should.push(dateQueryEnd);
        query.query.bool.must.bool.filter.bool.must.push(dateOrQuery);
      } else {
        if (data.beginDate) {
          let dateDebutQuery = {
            range: {
              entryDate: {
                from: data.beginDate,
              },
            },
          };
          let dateFinQuery = {
            range: {
              endDate: {
                from: data.beginDate,
              },
            },
          };
          dateOrQuery.bool.should.push(dateDebutQuery);
          dateOrQuery.bool.should.push(dateFinQuery);
          query.query.bool.must.bool.filter.bool.must.push(dateOrQuery);
        }
        if (data.endDate) {
          let dateDebutQuery = {
            range: {
              entryDate: {
                to: data.endDate,
              },
            },
          };
          let dateFinQuery = {
            range: {
              endDate: {
                to: data.endDate,
              },
            },
          };
          dateOrQuery.bool.should.push(dateDebutQuery);
          dateOrQuery.bool.should.push(dateFinQuery);
          query.query.bool.must.bool.filter.bool.must.push(dateOrQuery);
        }
      }

      if (data.firstNameFilter) {
        let firstNameQuery = {
          bool: {
            should: {
              regexp: {
                'contactFirstName.keyword': `.*${data.firstNameFilter.toLowerCase()}.*`,
              },
            },
          },
        };
        query.query.bool.must.bool.filter.bool.must.push(firstNameQuery);
      }
      if (data.lastNameFilter) {
        let lastNameQuery = {
          bool: {
            should: {
              regexp: {
                'contactLastName.keyword': `.*${data.lastNameFilter.toLowerCase()}.*`,
              },
            },
          },
        };
        query.query.bool.must.bool.filter.bool.must.push(lastNameQuery);
      }

      if (data.uriFilter) {
        let uriQuery = {
          bool: {
            should: {
              regexp: {
                contactUri: `.*${data.uriFilter.toLowerCase()}.*`,
              },
            },
          },
        };
        query.query.bool.must.bool.filter.bool.must.push(uriQuery);
      }
      return query;
    }
  }

  private buildContactEntries(data): Interaction[] {
    if (
      !!data &&
      isDefined(data.hits) &&
      isDefined(data.hits.total) &&
      isDefined(data.hits.total.value) &&
      data.hits.total.value > 0 &&
      isArray(data.hits.hits)
    ) {
      return data.hits.hits.map(hit => ({
        id: hit._id,
        entryDate: hit._source.entryDate,
        canalName: hit._source.canalName,
        canalDirection: hit._source.canalDirection,
        contactUri: hit._source.contactUri,
        civility: hit._source.civility,
        contactFirstName: hit._source.contactFirstName,
        contactLastName: hit._source.contactLastName,
        contactUuid: hit._source.contactUuid,
        csrUuid: hit._source.csrUuid,
        csrLogin: hit._source.csrLogin,
        csrFirstname: hit._source.csrFirstName,
        csrLastname: hit._source.csrLastName,
        skills: hit._source.skills,
        campaignType: hit._source.campaignType,
        description: hit._source.description,
        technicalId: hit._source.technicalId,
        interactionType: hit._source.interactionType || '',
        contents: hit._source.contents,
        hasRecord: false,
        record: undefined,
        consent: hit._source.consent,
        sort: hit.sort,
      }));
    }
    return [];
  }

  execContactEntryRequest(
    json,
    from,
    size,
    flowsUuid,
    skillsUuid,
    domainUuidList,
    teamsUuid,
    sortName,
    sortOrder
  ): Observable<InteractionResult> {
    let searchParams: any = this.buildContactEntryRequest(
      json,
      (from - 1) * size,
      size,
      flowsUuid,
      skillsUuid,
      domainUuidList,
      teamsUuid,
      sortName,
      sortOrder
    );

    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ({
        contactEntries: this.buildContactEntries(data),
        total: !!data && isDefined(data.hits) && isDefined(data.hits.total) ? data.hits.total.value : 0,
      })),
      catchError(error => throwError(() => new Error(`[HistoryService] execContactEntryRequest failed :[${JSON.stringify(error)}]`)))
    );
  }

  private buildRecordRequest(technicalId) {
    return {
      types: ['record'],
      track_total_hits: true,
      query: {
        term: { channelId: technicalId },
      },
    };
  }

  private buildRecord(data): CsrHistory | undefined {
    let csrHistory: CsrHistory[];
    csrHistory = this.buildCsrList(data);
    return csrHistory && csrHistory.length > 0 ? csrHistory[0] : undefined;
  }

  execSearchRecordFromInteractionRequest(technicalId: string): Observable<CsrHistory | undefined> {
    let searchParams: any = this.buildRecordRequest(technicalId);
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(response => this.buildRecord(response)),
      catchError(error =>
        throwError(() => new Error(`[HistoryService] execSearchRecordFromInteractionRequest failed :[${JSON.stringify(error)}]`))
      )
    );
  }

  isRecordAvailable(technicalId: string): Observable<boolean> {
    if (!isDefined(technicalId)) return of(false);
    return RxUtil.fromPromise(() => this.recordDeprecatedService.getRecordStatus(technicalId)).pipe(
      map(data => data === 'AVAILABLE' || data === 'OUT_STRATEGY' || data === 'RESTRICTED'),
      catchError(() => of(false))
    );
  }

  execContactRequest(json, from = 0, size = 10000): Observable<ContactHistory[]> {
    let searchParams: any;
    if (json.uuidFilter != null && json.uuidFilter !== '') {
      searchParams = this.buildOneContactByUuidRequest(json.uuidFilter);
    } else {
      searchParams = this.buildContactRequest(json, from, size);
    }

    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(response => this.buildContactHistory(response)),
      catchError(err => throwError(() => new Error(`[HistoryService] execContactRequest failed :[${JSON.stringify(err)}]`)))
    );
  }
}

export default 'historyDeprecatedService';
